// Enregistrement d'un courrier avec numérisation (dépôt des fichiers), brouillon automatique et
// confidentialité. Le numéro est attribué par le serveur.
import type { CourrierDetail } from '@cc/contrats/ui-metier';
import { Bandeau, Bouton, CaseACocher, Champ, DateChamp, FichierChamp, Formulaire, GrilleChamps, Panneau, Selection, ZoneTexte, useBrouillon } from '@cc/ui';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { requete } from '../../api/client';
import { ErreurApi, messageErreur } from '../../api/erreurs';
import { EnTetePage } from '../../commun/EnTetePage';
import { AffichageErreur } from '../../commun/EtatRequete';
import { ModuleNonActive, useModuleActif } from '../../commun/GardeModule';
import { isoJour } from '../../commun/format';
import { useMessages } from '../../commun/Messages';
import { deposerDocument, TAILLE_MAX_OCTETS, TYPES_ACCEPTES } from './listes';

interface Saisie {
  sens: 'arrivee' | 'depart';
  objet: string;
  correspondant_nom: string;
  correspondant_type: 'personne' | 'organisme';
  correspondant_adresse: string;
  date_courrier: string;
  canal: 'papier' | 'courriel' | 'portail' | 'main_propre' | 'poste';
  priorite: 'normale' | 'urgente';
  confidentialite: 'ordinaire' | 'restreint';
}

const VIDE: Saisie = { sens: 'arrivee', objet: '', correspondant_nom: '', correspondant_type: 'personne', correspondant_adresse: '', date_courrier: isoJour(new Date()), canal: 'papier', priorite: 'normale', confidentialite: 'ordinaire' };

export function PageNouveauCourrier() {
  const navigate = useNavigate();
  const { afficher } = useMessages();
  const [s, setS] = useState<Saisie>(VIDE);
  const [fichiers, setFichiers] = useState<File[]>([]);
  const brouillon = useBrouillon('courrier-nouveau', s);
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<unknown>(null);
  const [erreursChamps, setErreursChamps] = useState<{ champ: string; message: string }[]>([]);
  const [pieces, setPieces] = useState<{ deposees: number; echecs: string[] } | null>(null);
  const moduleCourriers = useModuleActif('M02');
  const maj = (cle: keyof Saisie) => (e: { target: { value: string } }) => setS((x) => ({ ...x, [cle]: e.target.value }));

  const soumettre = async () => {
    const manquants = [
      !s.objet.trim() ? { champ: 'courrier-objet', message: 'L\'objet est obligatoire' } : null,
      !s.correspondant_nom.trim() ? { champ: 'courrier-correspondant-nom', message: 'Le correspondant est obligatoire' } : null,
    ].filter((x): x is { champ: string; message: string } => x !== null);
    setErreursChamps(manquants);
    if (manquants.length > 0) return;
    setErreur(null);
    setEnCours(true);
    try {
      const cree = await requete<CourrierDetail>('/courriers', {
        methode: 'POST',
        corps: {
          sens: s.sens,
          objet: s.objet,
          correspondant_nom: s.correspondant_nom,
          correspondant_type: s.correspondant_type,
          correspondant_adresse: s.correspondant_adresse || undefined,
          date_courrier: s.date_courrier || undefined,
          canal: s.canal,
          priorite: s.priorite,
          confidentialite: s.confidentialite,
        },
      });
      const echecs: string[] = [];
      for (const f of fichiers) {
        try {
          await deposerDocument(f, 'courrier', cree.id);
        } catch (e) {
          echecs.push(`${f.name} : ${messageErreur(e)}`);
        }
      }
      brouillon.effacer();
      afficher('succes', `Courrier enregistré sous le numéro ${cree.numero ?? '(numéro en cours d\'attribution)'}.`);
      if (echecs.length > 0) {
        setPieces({ deposees: fichiers.length - echecs.length, echecs });
        afficher('erreur', `${echecs.length} pièce(s) non déposée(s) : ajoutez-les depuis la fiche du courrier.`);
      }
      navigate(`/courriers/${cree.id}`);
    } catch (e) {
      setErreur(e);
      if (e instanceof ErreurApi) setErreursChamps(e.champs.map((c) => ({ champ: `courrier-${c.champ.replace(/_/g, '-')}`, message: c.message })));
    } finally {
      setEnCours(false);
    }
  };

  // Module M02 désactivé (H9-01) : aucun nouveau courrier ; la liste et les fiches restent lisibles.
  if (moduleCourriers === false) return <ModuleNonActive module="M02" retour={{ href: '/courriers', libelle: 'Courriers' }} />;

  return (
    <>
      <EnTetePage etiquette="Courriers" titre="Enregistrer un courrier" description="Le numéro d'enregistrement est attribué par le serveur à la validation du formulaire." />
      {erreur instanceof ErreurApi && erreur.routeIndisponible ? (
        <AffichageErreur erreur={erreur} lot="L0-C" fonction="L'enregistrement des courriers" />
      ) : erreur && erreursChamps.length === 0 ? (
        <Bandeau type="erreur">{messageErreur(erreur)}</Bandeau>
      ) : null}
      {pieces ? <Bandeau type="attention">{pieces.echecs.join(' ; ')}</Bandeau> : null}
      <Panneau>
        <Formulaire
          libelle="Nouveau courrier"
          onSoumettre={() => void soumettre()}
          erreurs={erreursChamps}
          brouillonEnregistreLe={brouillon.enregistreLe}
          restauration={
            brouillon.brouillonDisponible
              ? { date: brouillon.brouillonDate, onReprendre: () => { setS({ ...VIDE, ...brouillon.brouillonDisponible }); brouillon.ignorer(); }, onIgnorer: brouillon.effacer }
              : null
          }
          actions={
            <>
              <Bouton type="submit" chargement={enCours} libelleChargement="Enregistrement en cours">
                Enregistrer le courrier
              </Bouton>
              <Bouton variante="secondaire" onClick={() => navigate('/courriers')}>
                Annuler
              </Bouton>
            </>
          }
        >
          <GrilleChamps>
            <Selection id="courrier-sens" libelle="Sens" value={s.sens} onChange={(e) => setS((x) => ({ ...x, sens: e.target.value as Saisie['sens'] }))} options={[{ valeur: 'arrivee', libelle: 'Arrivée' }, { valeur: 'depart', libelle: 'Départ' }]} required />
            <DateChamp id="courrier-date-courrier" libelle="Date portée sur le courrier" value={s.date_courrier} onChange={maj('date_courrier')} facultatif />
            <Selection
              id="courrier-canal"
              libelle="Canal"
              value={s.canal}
              onChange={(e) => setS((x) => ({ ...x, canal: e.target.value as Saisie['canal'] }))}
              options={[
                { valeur: 'papier', libelle: 'Papier numérisé' },
                { valeur: 'main_propre', libelle: 'Remis en main propre' },
                { valeur: 'poste', libelle: 'Voie postale' },
                { valeur: 'courriel', libelle: 'Courriel' },
                { valeur: 'portail', libelle: 'Portail' },
              ]}
            />
            <Selection id="courrier-priorite" libelle="Priorité" value={s.priorite} onChange={(e) => setS((x) => ({ ...x, priorite: e.target.value as Saisie['priorite'] }))} options={[{ valeur: 'normale', libelle: 'Normale' }, { valeur: 'urgente', libelle: 'Urgente' }]} />
          </GrilleChamps>
          <Champ id="courrier-objet" libelle="Objet" value={s.objet} onChange={maj('objet')} erreur={erreursChamps.find((e) => e.champ === 'courrier-objet')?.message} required />
          <GrilleChamps>
            <Champ id="courrier-correspondant-nom" libelle={s.sens === 'arrivee' ? 'Expéditeur' : 'Destinataire'} value={s.correspondant_nom} onChange={maj('correspondant_nom')} erreur={erreursChamps.find((e) => e.champ === 'courrier-correspondant-nom')?.message} required />
            <Selection id="courrier-correspondant-type" libelle="Nature du correspondant" value={s.correspondant_type} onChange={(e) => setS((x) => ({ ...x, correspondant_type: e.target.value as Saisie['correspondant_type'] }))} options={[{ valeur: 'personne', libelle: 'Personne' }, { valeur: 'organisme', libelle: 'Organisme' }]} />
          </GrilleChamps>
          <ZoneTexte id="courrier-correspondant-adresse" libelle="Adresse du correspondant" value={s.correspondant_adresse} onChange={maj('correspondant_adresse')} facultatif />
          <CaseACocher libelle="Diffusion restreinte (service affectataire, secrétaire général, maire ou président)" checked={s.confidentialite === 'restreint'} onChange={(e) => setS((x) => ({ ...x, confidentialite: e.target.checked ? 'restreint' : 'ordinaire' }))} />
          <FichierChamp
            libelle="Numérisation et pièces jointes"
            aide="PDF, JPEG, PNG, DOCX, XLSX ou ODT, 10 Mo au plus par fichier. Les fichiers sont déposés après l'enregistrement et ne sont pas conservés dans le brouillon."
            fichiers={fichiers}
            onFichiers={setFichiers}
            accept={TYPES_ACCEPTES}
            tailleMax={TAILLE_MAX_OCTETS}
            facultatif
          />
        </Formulaire>
      </Panneau>
    </>
  );
}
